/**
 * acceptInvitation — RPC contract for the callable that joins a person to
 * an organization they were invited to.
 *
 * Signing in proves an address; agreeing is a separate act, and this is
 * that act. `invite.entity.ts` marks `accept` `.requiresHandler(
 * 'acceptInvitation')`: the authority is being the addressee — the caller's
 * proven address is the one the invite names — and the caller holds no
 * grant at the organization yet, so no declarative rule evaluated under
 * their permissions can admit the grant write. The handler finds the
 * invite by that address under the named organization, re-reads it inside
 * one transaction so a revoke wins a race, writes the grant(s) the invite's
 * scope names, flips the invite to `redeemed` and deletes the announcement.
 *
 * Fields justified:
 *   orgId — the organization whose invitation is being answered. The
 *           invitation names no token on the wire (the announcement's own
 *           doc id is one, but no action spends it) — an address invited to
 *           two organizations answers twice.
 *   platformTerms — the operator's platform terms and privacy notice versions
 *           the person accepted with this act, as shown off `platform/legal`.
 *           Required when the invitation is the owner invitation a staff
 *           approval founded the organization with: accepting it concludes
 *           the founder's contract with the operator. The handler checks
 *           both against the current record and appends them to
 *           `users/{uid}.legalConsents` in the accept's own transaction.
 *           Ignored on every other invitation.
 *
 * Output: `{ ok: true }` — the grant the call wrote is the session's own
 * `users/{uid}/grants/{orgId}` listener's to pick up. A second accept of an
 * invite this caller already redeemed answers the same, writing nothing.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated — no session.
 *   failed-precondition / email-not-verified — the session's address is not
 *     one the platform vouched for; nothing is looked up.
 *   failed-precondition / <InvitationRefusal> — nothing pending for the
 *     proven address at this organization, and why. Closed so the client
 *     maps each member to copy rather than matching prose.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

/**
 * Why answering an invitation refuses, read off the invite's own state —
 * shared with declineInvitation.action.ts, which imports it from here.
 *
 *   no-invitation     — no invite at this organization names the proven
 *                       address.
 *   invite-unreadable — an invite names it but does not parse, or its scope
 *                       names another organization: a document to repair,
 *                       not an offer to answer.
 *   invite-redeemed   — already accepted; the person is in.
 *   invite-declined   — already turned down by the person.
 *   invite-revoked    — withdrawn by the organization.
 *   invite-expired    — the 14-day window passed before an answer.
 *   platform-terms    — the invitation founds a staff-approved organization,
 *                       and the accept did not carry acceptance of the
 *                       operator's current platform terms and privacy notice
 *                       (absent, stale against `platform/legal`, or no
 *                       record to accept). Accept only.
 */
export const InvitationRefusal = z.enum([
	"no-invitation",
	"invite-unreadable",
	"invite-redeemed",
	"invite-declined",
	"invite-revoked",
	"invite-expired",
	"platform-terms",
]);
export type InvitationRefusal = z.infer<typeof InvitationRefusal>;

export const AcceptInvitationInput = z
	.object({
		orgId: Id.describe("the organization whose invitation is being accepted"),
		platformTerms: z
			.object({
				tosVersion: z.string().min(1),
				privacyVersion: z.string().min(1),
			})
			.strict()
			.nullish()
			.describe(
				"the operator's platform terms and privacy notice versions accepted with a founding invitation",
			),
	})
	.strict();
export type AcceptInvitationInput = z.infer<typeof AcceptInvitationInput>;

export const AcceptInvitationOutput = z
	.object({ ok: z.literal(true) })
	.strict();
export type AcceptInvitationOutput = z.infer<typeof AcceptInvitationOutput>;
